import DotsHorizontal from "@redotech/redo-icons/dots-horizontal.svg";
import { ObjectId } from "bson";
import classNames from "classnames";
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import { TextColorValue } from "../../theme/typography";
import { OverflowTooltip } from "../../tooltip/overflow-tooltip";
import { Tooltip } from "../../tooltip/tooltip";
import { RedoBadge, RedoBadgeProps } from "../badge/redo-badge";
import { RedoButton, RedoButtonProps } from "../buttons/redo-button";
import { CheckboxValue, RedoCheckbox } from "../checkbox/redo-checkbox";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "../command-menu/redo-command-menu";
import {
  RedoFeaturedIcon,
  RedoFeaturedIconProps,
} from "../featured-icon/redo-featured-icon";
import { RedoSpinner, RedoSpinnerSize } from "../loading/redo-spinner";
import * as redoTableCellsCss from "./redo-table-cells.module.css";
import * as redoTableRowCss from "./redo-table-row.module.css";

const CommandMenuContext = createContext<{
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
} | null>(null);

export function CommandMenuProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  return (
    <CommandMenuContext.Provider value={{ openMenuId, setOpenMenuId }}>
      {children}
    </CommandMenuContext.Provider>
  );
}

export type RedoTableCellProps<DATA> = { row: DATA; rowIndex: number };

/** Takes a way to convert rows into the usable data for the text cell, and returns a way to create a text cell */
type TextCellProps = {
  text: string;
  subtext?: string;
  subtext2?: string;
  leading?: boolean;
  textColor?: TextColorValue | undefined;
  badges?: RedoBadgeProps[];
};

type FeaturedIconTextCellProps = TextCellProps & {
  icon: {
    text: string;
    color: RedoFeaturedIconProps["color"];
    size: RedoFeaturedIconProps["size"];
    shape: RedoFeaturedIconProps["shape"];
  };
};

export const RedoTableTextCell = memo(function RedoTableTextCell({
  text,
  subtext,
  subtext2,
  leading,
  textColor,
  badges,
}: TextCellProps) {
  const styleToArgs = isLeadingToArgsMap(leading, subtext);
  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
  return (
    <Flex
      className={classNames(
        redoTableCellsCss.overflowableTextContainer,
        redoTableCellsCss.cellContainer,
      )}
      dir="column"
      gap="xxs"
    >
      <OverflowTooltip overflowRef={overflowRef} tooltipProps={{ title: text }}>
        <Flex align="center" gap="sm">
          <Text
            className={redoTableCellsCss.overflowableText}
            fontSize={styleToArgs.mainFontSize}
            fontWeight={styleToArgs.mainFontWeight}
            ref={setOverflowRef}
            textColor={textColor ?? styleToArgs.mainTextColor}
          >
            {text}
          </Text>
          <Flex align="center" gap="xs">
            {badges &&
              badges.map((badge, index) => (
                <RedoBadge {...badge} key={index} />
              ))}
          </Flex>
        </Flex>
      </OverflowTooltip>
      {subtext && (
        <Text
          className={redoTableCellsCss.overflowableText}
          fontSize={styleToArgs.subFontSize}
          textColor={textColor ?? styleToArgs.subTextColor}
        >
          {subtext}
        </Text>
      )}
      {subtext2 && (
        <Text
          className={redoTableCellsCss.overflowableText}
          fontSize={styleToArgs.subFontSize}
          textColor={textColor ?? styleToArgs.subTextColor}
        >
          {subtext2}
        </Text>
      )}
    </Flex>
  );
});

interface IRedoTableTextArrayCellProps<T> {
  items: T[] | undefined;
  formatItem?: (item: T) => string;
  emptyText?: string;
}

export const RedoTableTextArrayCell = <T,>({
  items,
  formatItem,
  emptyText = "---",
}: IRedoTableTextArrayCellProps<T>) => {
  if (!items || items.length === 0) {
    return <RedoTableTextCell text={emptyText} />;
  }

  const formatItemText = (item: T) => {
    return formatItem ? formatItem(item) : String(item);
  };

  if (items.length === 1) {
    return <RedoTableTextCell text={formatItemText(items[0])} />;
  }

  return (
    <RedoTablePaddedCell>
      <ShadcnTooltip>
        <TooltipTrigger onClick={(e) => e.stopPropagation()}>
          <RedoBadge size="sm" text={`+${items.length}`} />
        </TooltipTrigger>
        <TooltipContent className="p-0" onClick={(e) => e.stopPropagation()}>
          {items.map((item, index) => (
            <div key={index}>
              <div className="p-2">
                <Text as="span">{formatItemText(item)}</Text>
              </div>
              {index < items.length - 1 && <Divider />}
            </div>
          ))}
        </TooltipContent>
      </ShadcnTooltip>
    </RedoTablePaddedCell>
  );
};

export const RedoTableFeaturedIconTextCell = memo(
  function RedoTableFeaturedIconTextCell({
    text,
    subtext,
    subtext2,
    leading,
    textColor,
    icon,
  }: FeaturedIconTextCellProps) {
    const styleToArgs = isLeadingToArgsMap(leading, subtext);
    const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
    const [subtitleoverflowRef, setSubtitleOverflowRef] =
      useState<HTMLDivElement | null>(null);
    return (
      <Flex
        align="center"
        className={classNames(
          redoTableCellsCss.cellContainer,
          redoTableCellsCss.featuredIconCellContainer,
        )}
        gap="sm"
      >
        <Flex className={redoTableCellsCss.featuredIconWrapper}>
          <RedoFeaturedIcon
            color={icon.color}
            Icon={() => <Text>{icon.text}</Text>}
            shape={icon.shape}
            size={icon.size}
          />
        </Flex>
        <Flex
          className={classNames(
            redoTableCellsCss.overflowableTextContainer,
            redoTableCellsCss.featuredIconTextContainer,
          )}
          dir="column"
          gap="sm"
        >
          <OverflowTooltip
            hideOnHover
            overflowRef={overflowRef}
            tooltipProps={{ title: text }}
          >
            <Text
              className={redoTableCellsCss.overflowableText}
              fontSize={styleToArgs.mainFontSize}
              fontWeight={styleToArgs.mainFontWeight}
              ref={setOverflowRef}
              textColor={textColor ?? styleToArgs.mainTextColor}
            >
              {text}
            </Text>
          </OverflowTooltip>
          {subtext && (
            <OverflowTooltip
              hideOnHover
              overflowRef={subtitleoverflowRef}
              tooltipProps={{ title: subtext }}
            >
              <Text
                className={redoTableCellsCss.overflowableText}
                fontSize={styleToArgs.subFontSize}
                ref={setSubtitleOverflowRef}
                textColor={textColor ?? styleToArgs.subTextColor}
              >
                {subtext}
              </Text>
            </OverflowTooltip>
          )}
          {subtext2 && (
            <Text
              className={redoTableCellsCss.overflowableText}
              fontSize={styleToArgs.subFontSize}
              textColor={textColor ?? styleToArgs.subTextColor}
            >
              {subtext2}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  },
);

const isLeadingToArgsMap = (leading?: boolean, subtext?: string): any => {
  if (leading) {
    return {
      mainFontSize: "xs",
      mainFontWeight: "medium",
      mainTextColor: "primary",
      subFontSize: "xxs",
      subFontWeight: "regular",
      subTextColor: "tertiary",
    };
  }
  return {
    mainFontSize: "xs",
    mainFontWeight: "regular",
    mainTextColor: subtext ? "primary" : "tertiary",
    subFontSize: "xxs",
    subFontWeight: "regular",
    subTextColor: "tertiary",
  };
};

export function RedoTableCheckboxCell({
  selected,
  disabled,
  setSelected,
}: {
  selected: CheckboxValue;
  disabled?: boolean;
  setSelected?: (selected: CheckboxValue) => void;
}) {
  const stopClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };
  return (
    <Flex
      align="center"
      as="label"
      className={classNames(redoTableCellsCss.rowCheckboxCell, {
        [redoTableCellsCss.disabled]: disabled,
      })}
      justify="center"
      onClick={stopClick}
      p="xl"
    >
      <RedoCheckbox
        disabled={disabled}
        setValue={(value) => {
          setSelected?.(value);
        }}
        size="sm"
        value={selected}
      />
    </Flex>
  );
}

export const RedoTableCommandMenuCell = memo(function RedoTableCommandMenuCell({
  items,
}: {
  items: RedoCommandMenuItem[];
}) {
  const context = useContext(CommandMenuContext)!;
  const menuId = useMemo(() => new ObjectId().toString(), []);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const menuOpen = context.openMenuId === menuId;

  return (
    <>
      <Flex px="3xl">
        <RedoButton
          className={redoTableRowCss.hoverable}
          IconLeading={DotsHorizontal}
          onClick={(e) => {
            e.stopPropagation();
            context.setOpenMenuId(menuOpen ? null : menuId);
          }}
          ref={setMenuAnchor}
        />
      </Flex>
      <RedoCommandMenu
        anchor={menuAnchor}
        items={items}
        open={menuOpen}
        setOpen={(open) => context.setOpenMenuId(open ? menuId : null)}
      />
    </>
  );
});

export const RedoTableToolbarCell = memo(function RedoTableButtonCell({
  buttons,
}: {
  buttons: Omit<RedoButtonProps, "size">[];
}) {
  return (
    <Flex align="center" justify="center" p="xl">
      {buttons.map((button, idx) => (
        <RedoButton
          {...button}
          className={redoTableRowCss.hoverable}
          key={idx}
          size="sm"
        />
      ))}
    </Flex>
  );
});

interface IRedoTableBadgeArrayCellProps {
  items?: RedoBadgeProps[];
  emptyText?: string;
}
/** A cell that displays a list of badges, with a +N badge if is more than 1 badge.
 * When hovering over the +N badge it will show a tooltip with all the badges in a list.
 **/
export const RedoTableBadgeArrayCell = ({
  items,
  emptyText = "---",
}: IRedoTableBadgeArrayCellProps) => {
  if (!items || items.length === 0) {
    return <RedoTableTextCell text={emptyText} />;
  }

  if (items.length === 1) {
    return (
      <RedoTablePaddedCell>
        <RedoBadge {...items[0]} />
      </RedoTablePaddedCell>
    );
  }

  return (
    <RedoTablePaddedCell>
      <ShadcnTooltip>
        <TooltipTrigger onClick={(e) => e.stopPropagation()}>
          <RedoBadge size="sm" text={`+${items.length}`} />
        </TooltipTrigger>
        <TooltipContent className="p-0" onClick={(e) => e.stopPropagation()}>
          {items.map((item, index) => (
            <div key={index}>
              <div className="p-2">
                <RedoBadge {...item} />
              </div>
              {index < items.length - 1 && <Divider />}
            </div>
          ))}
        </TooltipContent>
      </ShadcnTooltip>
    </RedoTablePaddedCell>
  );
};

/** A cell that displays a list of tags, with a +N badge if there are more tags than can fit.
 * Designed to be used with RedoTableWidthMode.FIXED columns.
 **/
export const RedoTableBadgeCell = memo(function RedoTableBadgeCell({
  badges,
  cellProps,
}: {
  badges: RedoBadgeProps[];
  cellProps?: { padding?: SpacingValue };
}) {
  const [cellRef, setCellRef] = useState<HTMLHeadingElement | null>(null);

  const [tagsToDisplay, setTagsToDisplay] = useState(badges);

  const [hiddenBadges, setHiddenBadges] = useState<RedoBadgeProps[]>([]);

  // Need to reset the internal state whenever the badges prop changes.
  // TODO: switch to useMemo and calculate the width instead of using internal state.
  useEffect(() => {
    setTagsToDisplay(badges);
    setHiddenBadges([]);
  }, [badges]);

  useEffect(() => {
    if (!cellRef) {
      return;
    }

    const actualWidth = cellRef.getBoundingClientRect().width || 0;
    const maxWidth =
      cellRef.parentElement?.getBoundingClientRect().width || Infinity;

    if (actualWidth > maxWidth && tagsToDisplay.length > 0) {
      setHiddenBadges((prev) => [
        ...prev,
        tagsToDisplay[tagsToDisplay.length - 1],
      ]);
      setTagsToDisplay((prevTags) => prevTags.slice(0, -1));
    }
  }, [cellRef, tagsToDisplay]);

  return (
    <Flex p={cellProps?.padding ?? "xl"} ref={setCellRef}>
      {tagsToDisplay.map((tag, idx) => (
        <RedoBadge {...tag} color={tag.color || "blueLight"} key={`${idx}`} />
      ))}
      {hiddenBadges.length > 0 && (
        <Tooltip title={hiddenBadges.map((badge) => badge.text).join(", ")}>
          {/* This div is needed to make the tooltip work */}
          <div>
            <RedoBadge color="gray" text={`+${hiddenBadges.length}`} />
          </div>
        </Tooltip>
      )}
    </Flex>
  );
});

export function RedoTableLoadingCell({
  size = "xs",
}: {
  size?: RedoSpinnerSize;
}) {
  return (
    <Flex
      className={classNames(
        redoTableCellsCss.overflowableTextContainer,
        redoTableCellsCss.cellContainer,
      )}
      dir="column"
      gap="xxs"
    >
      <RedoSpinner size={size} />
    </Flex>
  );
}

export function RedoTablePaddedCell({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Flex flex={1} p="xl">
      {children}
    </Flex>
  );
}
